import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  format,
  eachDayOfInterval,
  isSunday,
  isSameDay,
  isWithinInterval,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  eachMonthOfInterval,
  subMonths,
  startOfYear,
  endOfYear,
  subYears,
} from 'date-fns';
import { CalendarIcon, Phone } from 'lucide-react';

import { cn, getLocalizedName } from '@/lib/utils';
import { getDateFnsLocale } from '@/lib/utils/date';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { ResultCoefficientCard } from '../components';
import { useKpiSummary, useFirmKpiSummary } from '../hooks';

/**
 * Fallback full-month hours norm, used only for the instant client-side estimate
 * shown in the date picker while the request is in flight. The authoritative
 * figure is each user's own `monthlyExpectedHours`, returned as
 * `period.expectedHours` (client review AL-2026/08-04-01, K-05).
 */
const MONTHLY_HOURS_FALLBACK = 160;

/**
 * Period presets — **standard calendar periods only** (TS Lot 3 §5.4: month,
 * quarter, half-year, year, plus a custom range).
 *
 * ⚠ The page used to open on a **rolling 31-day window** and offered
 * week/last-month presets. The client review called that out twice: a rolling
 * window is not one of the standard periods (remark C5), and the dashboard
 * opened on a *different* rolling window, so the two pages disagreed for
 * "the same" period. The default is now the **current calendar month**.
 */
const RANGE_PRESETS: {
  labelKey: string;
  range: (now: Date) => { from: Date; to: Date };
}[] = [
  {
    labelKey: 'time.thisMonth',
    range: (now) => ({ from: startOfMonth(now), to: endOfMonth(now) }),
  },
  {
    labelKey: 'time.lastMonth',
    range: (now) => {
      const d = subMonths(now, 1);
      return { from: startOfMonth(d), to: endOfMonth(d) };
    },
  },
  {
    labelKey: 'time.thisQuarter',
    range: (now) => ({ from: startOfQuarter(now), to: endOfQuarter(now) }),
  },
  {
    labelKey: 'time.thisHalfYear',
    range: (now) => {
      const half = now.getMonth() < 6 ? 0 : 6;
      return {
        from: new Date(now.getFullYear(), half, 1),
        to: endOfMonth(new Date(now.getFullYear(), half + 5, 1)),
      };
    },
  },
  {
    labelKey: 'time.thisYear',
    range: (now) => ({ from: startOfYear(now), to: endOfYear(now) }),
  },
  {
    labelKey: 'time.lastYear',
    range: (now) => {
      const d = subYears(now, 1);
      return { from: startOfYear(d), to: endOfYear(d) };
    },
  },
];

const DASH = '—';
// Parse a `yyyy-MM-dd` query param into a local Date (avoids the UTC shift of `new Date(str)`).
const parseYmd = (s: string | null): Date | undefined => {
  if (!s) return undefined;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};
const pct = (v: number | null | undefined) => (v == null ? DASH : `${v}%`);
const num = (v: number | null | undefined) => (v == null ? DASH : v);
const money = (v: number | null | undefined) =>
  v == null ? DASH : `$${v.toLocaleString()}`;

/**
 * One metric line. `hint` carries the numbers behind the value (counts, units,
 * the basis of a target) — the client's display rule D1: every percentage must
 * show what it was computed from, or nobody can check it.
 */
function StatRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <span className="min-w-0 shrink-0 text-muted-foreground">{label}</span>
      {/* The hint can be a full sentence (e.g. what "Revenue" is a price of),
          so it is width-capped and wraps under the value instead of running
          into the label. */}
      <span className="flex min-w-0 flex-col items-end text-right">
        <span className="font-medium tabular-nums">{value}</span>
        {hint != null && (
          <span className="mt-0.5 max-w-[15rem] text-[11px] leading-tight text-slate-400">
            {hint}
          </span>
        )}
      </span>
    </div>
  );
}

/** A pillar score as a labelled meter. Stacks label-over-bar so four fit in a row. */
function PillarBar({ label, value }: { label: string; value: number | null }) {
  const v = value ?? 0;
  const tone =
    value == null
      ? 'bg-slate-200'
      : v >= 75
        ? 'bg-emerald-500'
        : v >= 50
          ? 'bg-amber-500'
          : 'bg-red-500';
  return (
    <div className="py-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-[0.8125rem] text-slate-600">{label}</span>
        <span
          className={cn(
            'shrink-0 text-sm font-semibold tabular-nums',
            value == null ? 'text-slate-400' : 'text-slate-900',
          )}
        >
          {value == null ? DASH : `${value}%`}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all duration-700 ${tone}`}
          style={{ width: `${Math.min(100, Math.max(0, v))}%` }}
        />
      </div>
    </div>
  );
}

export function KPIsPage() {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const user = useAuthStore((state) => state.user);

  const canPickUser = hasAccess('kpi', 'get:all');
  // Finance figures (revenue, bonus, financial score) are MD-only.
  const isMd = user?.role?.type === 'MD';

  // userId + date range are mirrored in the URL query so the page is linkable
  // (e.g. the dashboard's Team Efficiency rows link straight here).
  const [searchParams, setSearchParams] = useSearchParams();

  const [userId, setUserId] = React.useState<number | undefined>(() => {
    // Only users who may pick others (kpi:get:all) can seed userId from the URL;
    // everyone else is pinned to their own id so a hand-edited ?userId= can't
    // surface another person's KPI. The backend enforces this too.
    // For pickers, no selection (undefined) means the firm-wide aggregate.
    if (!canPickUser) return user?.id;
    const q = searchParams.get('userId');
    return q ? Number(q) : undefined;
  });
  const [range, setRange] = React.useState<{ from?: Date; to?: Date }>(() => {
    const from = parseYmd(searchParams.get('from'));
    const to = parseYmd(searchParams.get('to'));
    if (from && to) return { from, to };
    // Default = the current calendar month, capped at today (TS Lot 3 §5.4).
    const now = new Date();
    const monthEnd = endOfMonth(now);
    return { from: startOfMonth(now), to: monthEnd > now ? now : monthEnd };
  });

  // Keep the URL in sync with the current selection.
  React.useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (userId != null) next.set('userId', String(userId));
    else next.delete('userId');
    if (range.from && range.to) {
      next.set('from', format(range.from, 'yyyy-MM-dd'));
      next.set('to', format(range.to, 'yyyy-MM-dd'));
    } else {
      next.delete('from');
      next.delete('to');
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, range.from, range.to]);
  // Extra (manually marked) days off — Sundays are always off and not stored here.
  const [daysOff, setDaysOff] = React.useState<Date[]>([]);
  const [pickerTab, setPickerTab] = React.useState<'period' | 'daysoff'>('period');

  const hasRange = Boolean(range.from && range.to);
  const activeTab = hasRange ? pickerTab : 'period';

  // A day is "off" if it's a Sunday or was manually marked off.
  const isDayOff = React.useCallback(
    (date: Date) => isSunday(date) || daysOff.some((d) => isSameDay(d, date)),
    [daysOff]
  );

  const isInRange = React.useCallback(
    (date: Date) =>
      hasRange &&
      isWithinInterval(date, {
        start: startOfDay(range.from!),
        end: startOfDay(range.to!),
      }),
    [hasRange, range.from, range.to]
  );

  /**
   * Working-days math driving the hours target (computed client-side for instant
   * feedback; the server is authoritative and mirrors this exactly).
   *
   * The norm is per **full month**, prorated by working days and summed MONTH BY
   * MONTH (K-05):
   *   for each month → norm × (its working days inside the period ÷ its working
   *   days in full)
   *
   * ⚠ Summing per month matters: a single whole-span ratio collapses to 1 for
   * any run of complete months, so a quarter would ask for one month's norm
   * instead of three.
   */
  const { workingDays, offCount, expectedHours } = React.useMemo(() => {
    if (!hasRange) return { workingDays: 0, offCount: 0, expectedHours: 0 };
    const from = range.from!;
    const to = range.to!;
    const allDays = eachDayOfInterval({ start: from, end: to });
    const off = allDays.filter((d) => isDayOff(d)).length;
    const working = Math.max(0, allDays.length - off);

    const countWorking = (start: Date, end: Date) =>
      eachDayOfInterval({ start, end }).filter((d) => !isDayOff(d)).length;

    const hours = eachMonthOfInterval({ start: from, end: to }).reduce(
      (sum, month) => {
        const mStart = startOfMonth(month);
        const mEnd = endOfMonth(month);
        const inMonth = countWorking(mStart, mEnd);
        if (inMonth === 0) return sum;
        const inSlice = countWorking(
          mStart < from ? from : mStart,
          mEnd > to ? to : mEnd,
        );
        return sum + (MONTHLY_HOURS_FALLBACK * inSlice) / inMonth;
      },
      0,
    );

    return {
      workingDays: working,
      offCount: off,
      expectedHours: Math.round(hours * 10) / 10,
    };
  }, [hasRange, range.from, range.to, isDayOff]);

  // ── Data ────────────────────────────────────────────────────────────────────────
  const daysOffParam = React.useMemo(
    () => daysOff.map((d) => startOfDay(d).toISOString()),
    [daysOff]
  );
  // No user selected + permission to view others → firm-wide aggregate.
  const firmMode = canPickUser && userId == null;
  const fromIso = hasRange ? startOfDay(range.from!).toISOString() : undefined;
  const toIso = hasRange ? endOfDay(range.to!).toISOString() : undefined;

  const userQuery = useKpiSummary(
    { userId: userId ?? 0, from: fromIso, to: toIso, daysOff: daysOffParam },
    !firmMode,
  );
  const firmQuery = useFirmKpiSummary(
    { from: fromIso, to: toIso, daysOff: daysOffParam },
    firmMode,
  );
  const data = firmMode ? firmQuery.data : userQuery.data;
  const isLoading = firmMode ? firmQuery.isLoading : userQuery.isLoading;

  const productivity = data?.productivity;
  const financial = data?.financial;
  const quality = data?.quality;
  const engagement = data?.engagement;
  const scores = data?.scores;

  // Prefer the server's per-user expected hours (each user has their own monthly
  // norm); fall back to the client estimate while the request is in flight.
  const displayExpectedHours = data?.period?.expectedHours ?? expectedHours;

  /**
   * The two "how was this figure derived" lines, built HERE from the server's
   * numbers rather than from the server's prose.
   *
   * The API also returns ready-made `expectedHoursBasis` / `targetBasis`
   * strings, but they are assembled in English on the backend and rendered
   * untranslated inside the Russian interface — QA found
   * "Цель: $500,000 / 313 working d × 4 working d" on 2026-08-05. Those fields
   * remain in the payload for the mobile client; the web UI translates instead.
   */
  const hoursBasis = React.useMemo(() => {
    const period = data?.period;
    if (period?.monthlyNorm == null || !period?.workingDaysInMonths) return undefined;
    return (period.normHeadcount ?? 1) > 1
      ? t('KPI.ts10.hoursBasisFirm', {
          norm: period.monthlyNorm,
          people: period.normHeadcount,
          days: period.workingDays,
          total: period.workingDaysInMonths,
        })
      : t('KPI.ts10.hoursBasis', {
          norm: period.monthlyNorm,
          days: period.workingDays,
          total: period.workingDaysInMonths,
        });
  }, [data?.period, t]);

  const targetBasis = React.useMemo(() => {
    const parts = financial?.targetBasisParts;
    if (!parts) return undefined;
    if (parts.kind === 'yearly-plan') {
      return t('KPI.ts10.targetBasisPlan', {
        amount: `$${parts.yearlyAmount.toLocaleString()}`,
        yearDays: parts.workingDaysInYear,
        days: parts.workingDaysInPeriod,
      });
    }
    return t('KPI.ts10.targetBasisRate', {
      rate: `$${parts.ratePerDay}`,
      days: parts.workingDays,
      role: parts.role ?? DASH,
    });
  }, [financial?.targetBasisParts, t]);

  // Cap current periods at today so "this month/year" stays month/year-to-date.
  const applyPreset = (preset: (typeof RANGE_PRESETS)[number]) => {
    const now = new Date();
    const { from, to } = preset.range(now);
    setRange({ from, to: to > now ? now : to });
    setPickerTab('period');
  };

  const dayOffModifiers = { dayoff: isDayOff };
  const dayOffModifierClassNames = { dayoff: 'text-red-500 font-semibold' };

  return (
    <div className="bg-[#F5F6F7] min-h-[calc(100vh-70px)]">
      {/* Header: who + when */}
      <div className="flex flex-wrap gap-2 py-2 px-4 justify-between items-center border-b border-gray-200 bg-white">
        {/* Title + who this KPI is for. The identity used to be a separate
            full-width card below, which duplicated the scope selector on the
            right and pushed the score down the page. */}
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-xl font-semibold">{t('KPI.kpi')}</h1>
          <span className="truncate text-base font-medium text-slate-700">
            {firmMode ? t('KPI.wholeFirm') : (data?.user?.name ?? DASH)}
          </span>
          {!firmMode && data?.user?.role && (
            <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
              {getLocalizedName(
                {
                  name: data.user.role.type ?? '',
                  name_en: data.user.role.name_en ?? undefined,
                  name_ru: data.user.role.name_ru ?? undefined,
                },
                i18n.language,
              )}
            </span>
          )}
          {!firmMode && data?.user?.phone && (
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
              <Phone className="h-3.5 w-3.5" />
              {data.user.phone}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canPickUser && (
            <UniversalCombobox
              url="/users"
              listUrl="/users/select-box"
              labelKey="name"
              valueKey="id"
              params={{ status: 'ACTIVE' }}
              selectedId={userId}
              onSelectItem={(selectedUser) =>
                setUserId(selectedUser ? selectedUser.id : undefined)
              }
              placeholder={t('KPI.wholeFirm')}
              className="w-[240px]"
            />
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-[280px] justify-between font-normal hover:bg-button-ground"
              >
                <span className="truncate">
                  {range.from && range.to
                    ? `${format(range.from, 'yyyy-MM-dd', { locale: dateFnsLocale })} - ${format(range.to, 'yyyy-MM-dd', { locale: dateFnsLocale })}`
                    : t('createdDateRange')}
                </span>
                <div className="flex items-center shrink-0">
                  <Separator orientation="vertical" className="mx-3 h-4" />
                  <CalendarIcon className="h-4 w-4" />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              {/* Tabs: pick the period, or mark days off within it */}
              <div className="p-3 pb-0">
                <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
                  <button
                    type="button"
                    onClick={() => setPickerTab('period')}
                    className={cn(
                      'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition',
                      activeTab === 'period'
                        ? 'bg-white shadow-sm'
                        : 'text-muted-foreground'
                    )}
                  >
                    {t('KPI.period')}
                  </button>
                  <button
                    type="button"
                    disabled={!hasRange}
                    onClick={() => setPickerTab('daysoff')}
                    className={cn(
                      'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition',
                      activeTab === 'daysoff'
                        ? 'bg-white shadow-sm'
                        : 'text-muted-foreground',
                      !hasRange && 'cursor-not-allowed opacity-40'
                    )}
                  >
                    {t('KPI.daysOff')}
                  </button>
                </div>
              </div>

              {activeTab === 'period' && (
                <div className="flex flex-wrap gap-1.5 px-3 pt-3">
                  {RANGE_PRESETS.map((preset) => (
                    <Button
                      key={preset.labelKey}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-full px-3 text-xs font-normal"
                      onClick={() => applyPreset(preset)}
                    >
                      {t(preset.labelKey)}
                    </Button>
                  ))}
                </div>
              )}

              {activeTab === 'period' ? (
                <Calendar
                  mode="range"
                  defaultMonth={range.from}
                  selected={{ from: range.from, to: range.to }}
                  onSelect={(r) => setRange({ from: r?.from, to: r?.to })}
                  numberOfMonths={2}
                  modifiers={dayOffModifiers}
                  modifiersClassNames={dayOffModifierClassNames}
                />
              ) : (
                <Calendar
                  mode="multiple"
                  defaultMonth={range.from}
                  selected={daysOff}
                  onSelect={(dates) => setDaysOff(dates ?? [])}
                  numberOfMonths={2}
                  // Sundays are always off; only days inside the period are editable.
                  disabled={(date) => isSunday(date) || !isInRange(date)}
                  modifiers={dayOffModifiers}
                  modifiersClassNames={dayOffModifierClassNames}
                />
              )}

              {/* Live working-days summary */}
              <div className="flex items-center justify-between gap-4 border-t px-3 py-2.5 text-sm">
                {hasRange ? (
                  <>
                    <div className="flex items-center gap-4">
                      <span>
                        {t('KPI.workingDays')}:{' '}
                        <b className="font-semibold">{workingDays}</b>
                      </span>
                      <span className="text-red-500">
                        {t('KPI.daysOff')}: <b className="font-semibold">{offCount}</b>
                      </span>
                    </div>
                    <span className="text-muted-foreground">
                      {t('KPI.expectedHours')}: {displayExpectedHours}h
                    </span>
                  </>
                ) : (
                  <span className="text-muted-foreground">
                    {t('KPI.selectPeriodFirst')}
                  </span>
                )}
              </div>

              {activeTab === 'daysoff' && (
                <p className="px-3 pb-3 text-xs text-muted-foreground">
                  {t('KPI.markDaysOffHint')}
                </p>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div
        className={cn(
          'p-4 flex flex-col gap-4 transition-opacity',
          isLoading && 'opacity-60'
        )}
      >
        {/* TS Lot 1 §10.1 — the contractual score. Page headline. */}
        <ResultCoefficientCard ts10={data?.ts10} isMd={isMd} firmMode={firmMode} />

        {/* ── Operational metrics ────────────────────────────────────────────
            Reference only (target design §3.5). Previously this was FOUR
            stacked white cards — an identity card, a score ring, three stat
            columns and an engagement card — which pushed the contractual score
            off screen and read as four unrelated panels. It is now one card:
            a compact pillar summary, then the metrics grouped in a grid, each
            with the numbers behind it. */}
        <div className="bg-white rounded-2xl drop-shadow-sm p-6">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                {t('KPI.ts10.operationalTitle')}
              </h2>
              <p className="mt-0.5 text-[0.8125rem] text-slate-500">
                {t('KPI.ts10.operationalNote')}
              </p>
            </div>
            {/* Composite kept as a small chip, not a second big dial competing
                with the contractual coefficient above. */}
            <div className="flex items-baseline gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {t('KPI.overall')}
              </span>
              <span className="text-lg font-bold tabular-nums text-slate-900">
                {scores?.overall == null ? DASH : `${scores.overall}%`}
              </span>
            </div>
          </div>

          <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
            <PillarBar label={t('KPI.productivity')} value={scores?.productivity ?? null} />
            {isMd && (
              <PillarBar label={t('KPI.financial')} value={scores?.financial ?? null} />
            )}
            <PillarBar label={t('KPI.quality')} value={scores?.quality ?? null} />
            <PillarBar label={t('KPI.engagement')} value={scores?.engagement ?? null} />
          </div>

          <div
            className={cn(
              'mt-5 grid grid-cols-1 gap-x-8 gap-y-5 border-t border-slate-100 pt-5',
              isMd ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
            )}
          >
            <div>
              <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {t('KPI.productivity')}
              </h3>
              <StatRow
                label={t('KPI.tasksDone')}
                value={num(productivity?.tasksDone)}
                hint={t('KPI.ts10.tasksUnit')}
              />
              <StatRow
                label={t('KPI.onTime')}
                value={pct(productivity?.onTimeRate)}
                hint={
                  productivity == null
                    ? undefined
                    : productivity.tasksWithDueDate > 0
                      ? t('KPI.ts10.ofTasks', {
                          done: productivity.tasksOnTime,
                          total: productivity.tasksWithDueDate,
                        })
                      : t('KPI.ts10.noDueDates')
                }
              />
              {/* K-05 — the norm no longer appears as a bare "/ 60h"; it states
                  the monthly figure and the working-day proration behind it.
                  ⚠ Built from the period's NUMBERS via i18n, not from the
                  server's `expectedHoursBasis` string — that one is English only
                  and was rendering untranslated in the Russian UI (QA 2026-08-05). */}
              <StatRow
                label={t('KPI.hoursLogged')}
                value={
                  productivity
                    ? `${productivity.hoursLogged}h${hasRange ? ` / ${displayExpectedHours}h` : ''}`
                    : DASH
                }
                hint={hoursBasis}
              />
              <StatRow
                label={t('KPI.avgTurnaround')}
                value={
                  productivity?.avgTurnaroundDays == null
                    ? DASH
                    : `${productivity.avgTurnaroundDays}${t('KPI.days')}`
                }
              />
            </div>

            {isMd && (
              <div>
                <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {t('KPI.financial')}
                </h3>
                {/* K-06 — the caption follows the scope selector. At firm scope
                    "this lawyer's own finished work" was simply untrue. */}
                <StatRow
                  label={t('KPI.revenue')}
                  value={money(financial?.revenue)}
                  hint={
                    firmMode
                      ? t('KPI.ts10.revenueSourceNoteFirm')
                      : t('KPI.ts10.revenueSourceNote')
                  }
                />
                <StatRow
                  label={t('KPI.vsTarget')}
                  value={pct(financial?.vsTarget)}
                  hint={targetBasis}
                />
                {/* K-01 — renamed from "Realization". That name is reserved for
                    the §10.3 ratio (Actual_Revenue ÷ Virtual_Cost); this is the
                    billable share of hours worked. */}
                <StatRow
                  label={t('KPI.billableShare')}
                  value={pct(financial?.billableShare)}
                  hint={
                    financial == null
                      ? undefined
                      : t('KPI.ts10.ofHours', {
                          used: financial.billableHours,
                          total: financial.billableOfHours,
                        })
                  }
                />
                <StatRow
                  label={t('KPI.nonBilled')}
                  value={num(financial?.nonBilled)}
                  hint={t('KPI.ts10.tasksUnit')}
                />
              </div>
            )}

            <div>
              <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {t('KPI.qualityClient')}
              </h3>
              <StatRow
                label={t('KPI.reviewAvg')}
                value={
                  quality?.reviewAvg == null
                    ? DASH
                    : `${quality.reviewAvg} / ${quality.reviewScale}`
                }
                hint={
                  quality == null
                    ? undefined
                    : t('KPI.ts10.reviewsCount', { count: quality.reviewCount })
                }
              />
              <StatRow
                label={t('KPI.nps')}
                value={
                  quality?.nps == null
                    ? DASH
                    : `${quality.nps > 0 ? '+' : ''}${quality.nps}`
                }
                hint={
                  quality == null
                    ? undefined
                    : t('KPI.ts10.npsResponses', { count: quality.npsResponses })
                }
              />
              <StatRow
                label={t('KPI.winRate')}
                value={pct(quality?.winRate)}
                hint={
                  quality == null
                    ? undefined
                    : t('KPI.ts10.closedMatters', { count: quality.closedMatters })
                }
              />
              <StatRow label={t('KPI.complaints')} value={num(quality?.complaints)} />
            </div>

            {/* Engagement — was its own card for three values. */}
            <div>
              <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {t('KPI.engagement')}
              </h3>
              {/* K-06 — at firm scope this is the roster's mean tenure, not one
                  person's, and the label has to say so. */}
              <StatRow
                label={firmMode ? t('KPI.tenureAvg') : t('KPI.tenure')}
                value={
                  engagement?.tenureYears == null
                    ? DASH
                    : `${engagement.tenureYears} ${t('KPI.years')}`
                }
                hint={
                  firmMode && data?.period?.normHeadcount
                    ? t('KPI.ts10.acrossPeople', {
                        count: data.period.normHeadcount,
                      })
                    : undefined
                }
              />
              {isMd && (
                <StatRow
                  label={t('KPI.monthlyBonus')}
                  value={money(engagement?.monthlyBonus)}
                />
              )}
              <StatRow
                label={t('KPI.coefficientResult')}
                value={num(engagement?.coefficient)}
              />
            </div>
          </div>

          {/* K-03 — the pillar algorithm, on screen. The client had to
              reconstruct it by arithmetic; it is now stated, including which
              displayed sub-metrics feed a pillar and which are reference only. */}
          <details className="mt-5 border-t border-slate-100 pt-4">
            <summary className="cursor-pointer text-[0.8125rem] font-medium text-slate-600 hover:text-slate-900">
              {t('KPI.ts10.howComputed')}
            </summary>
            <div className="mt-3 space-y-2.5 text-[0.75rem] leading-relaxed text-slate-500">
              <p>
                <span className="font-semibold text-slate-700">
                  {t('KPI.overall')}
                </span>{' '}
                = {t('KPI.ts10.overallFormula')}
              </p>
              <ul className="space-y-1.5">
                <li>
                  <span className="font-medium text-slate-600">
                    {t('KPI.productivity')}
                  </span>{' '}
                  — {t('KPI.ts10.pillarProductivity')}
                </li>
                {isMd && (
                  <li>
                    <span className="font-medium text-slate-600">
                      {t('KPI.financial')}
                    </span>{' '}
                    — {t('KPI.ts10.pillarFinancial')}
                  </li>
                )}
                <li>
                  <span className="font-medium text-slate-600">
                    {t('KPI.quality')}
                  </span>{' '}
                  — {t('KPI.ts10.pillarQuality')}
                </li>
                <li>
                  <span className="font-medium text-slate-600">
                    {t('KPI.engagement')}
                  </span>{' '}
                  — {t('KPI.ts10.pillarEngagement')}
                </li>
              </ul>
              <p className="text-slate-400">{t('KPI.ts10.pillarNotFed')}</p>
              <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-800">
                {t('KPI.ts10.pillarDoubleCount')}
              </p>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
